import {
  ALL_FORMATS,
  EncodedPacketSink,
  Input,
  Mp4OutputFormat,
  UrlSource,
  type AudioCodec,
  type EncodedPacket
} from 'mediabunny';
import {
  getRenderAbortError,
  throwIfRenderAborted,
  waitForRenderOperation
} from './RenderTask';

const AUDIO_INPUT_CACHE_BYTES = 16 * 1024 * 1024;

export interface PreparedUserAudioTrack {
  codec: AudioCodec;
  decoderConfig: AudioDecoderConfig;
  maximumPacketCount: number;
  estimatedBytes: number;
  writeTo: (
    writePacket: (
      packet: EncodedPacket,
      metadata?: EncodedAudioChunkMetadata
    ) => Promise<void>,
    signal?: AbortSignal
  ) => Promise<void>;
  dispose: () => void;
}

/**
 * Selects one user soundtrack deterministically. The primary screen wins; the
 * secondary screen is only used when the primary video has no audio track.
 */
export async function prepareFirstUserAudioTrack(
  sourceUrls: readonly (string | undefined)[],
  outputDurationSeconds: number,
  signal?: AbortSignal,
  enabled = true
): Promise<PreparedUserAudioTrack | null> {
  if (!enabled) return null;
  if (!Number.isFinite(outputDurationSeconds) || outputDurationSeconds <= 0) {
    throw new TypeError('outputDurationSeconds must be a positive number.');
  }

  const uniqueUrls = Array.from(new Set(sourceUrls.filter((url): url is string => Boolean(url))));
  for (const sourceUrl of uniqueUrls) {
    const prepared = await prepareUserAudioTrack(sourceUrl, outputDurationSeconds, signal);
    if (prepared) {
      return prepared;
    }
  }
  return null;
}

async function prepareUserAudioTrack(
  sourceUrl: string,
  outputDurationSeconds: number,
  signal?: AbortSignal
): Promise<PreparedUserAudioTrack | null> {
  throwIfRenderAborted(signal);
  const input = new Input({
    formats: ALL_FORMATS,
    source: new UrlSource(sourceUrl, {
      maxCacheSize: AUDIO_INPUT_CACHE_BYTES,
      parallelism: 1
    })
  });
  const disposeOnAbort = () => input.dispose();
  signal?.addEventListener('abort', disposeOnAbort, { once: true });

  try {
    const track = await waitForRenderOperation(input.getPrimaryAudioTrack(), {
      signal,
      label: 'User audio track inspection'
    });
    if (!track) {
      input.dispose();
      return null;
    }

    const [codec, decoderConfig, sourceStartTimestamp] = await waitForRenderOperation(
      Promise.all([
        track.getCodec(),
        track.getDecoderConfig(),
        input.getFirstTimestamp()
      ]),
      {
        signal,
        label: 'User audio metadata loading'
      }
    );
    if (!codec || !decoderConfig) {
      throw new Error('The uploaded video audio track could not be read.');
    }
    if (!new Mp4OutputFormat().getSupportedAudioCodecs().includes(codec)) {
      throw new Error(`The uploaded video uses an unsupported audio codec (${codec}).`);
    }

    const packetStats = await countOutputPackets(
      new EncodedPacketSink(track),
      sourceStartTimestamp,
      outputDurationSeconds,
      signal
    );
    if (packetStats.count === 0) {
      input.dispose();
      return null;
    }

    let disposed = false;
    const dispose = () => {
      if (disposed) {
        return;
      }
      disposed = true;
      signal?.removeEventListener('abort', disposeOnAbort);
      input.dispose();
    };

    return {
      codec,
      decoderConfig,
      maximumPacketCount: packetStats.count,
      estimatedBytes: Math.ceil(packetStats.bytes * 1.1),
      writeTo: async (writePacket, writeSignal = signal) => {
        throwIfRenderAborted(writeSignal);
        const disposeOnWriteAbort = () => input.dispose();
        writeSignal?.addEventListener('abort', disposeOnWriteAbort, { once: true });
        let writtenPackets = 0;
        try {
          const sink = new EncodedPacketSink(track);
          for await (const packet of sink.packets()) {
            throwIfRenderAborted(writeSignal);
            const normalized = normalizePacketTiming(
              packet,
              sourceStartTimestamp,
              outputDurationSeconds
            );
            if (!normalized) {
              continue;
            }
            await waitForRenderOperation(
              writePacket(
                normalized,
                writtenPackets === 0 ? { decoderConfig } : undefined
              ),
              {
                signal: writeSignal,
                label: 'User audio packet write'
              }
            );
            writtenPackets += 1;
          }
          if (writtenPackets !== packetStats.count) {
            throw new Error(
              `User audio produced ${writtenPackets} of ${packetStats.count} expected packets.`
            );
          }
        } catch (error) {
          throwIfRenderAborted(writeSignal);
          throw error;
        } finally {
          writeSignal?.removeEventListener('abort', disposeOnWriteAbort);
        }
      },
      dispose
    };
  } catch (error) {
    input.dispose();
    throwIfRenderAborted(signal);
    throw error;
  } finally {
    signal?.removeEventListener('abort', disposeOnAbort);
  }
}

async function countOutputPackets(
  sink: EncodedPacketSink,
  sourceStartTimestamp: number,
  outputDurationSeconds: number,
  signal?: AbortSignal
): Promise<{ count: number; bytes: number }> {
  let count = 0;
  let bytes = 0;
  try {
    for await (const packet of sink.packets(undefined, undefined, { metadataOnly: true })) {
      throwIfRenderAborted(signal);
      if (!normalizePacketTiming(packet, sourceStartTimestamp, outputDurationSeconds)) {
        continue;
      }
      count += 1;
      bytes += packet.byteLength;
    }
  } catch (error) {
    if (signal?.aborted) {
      throw getRenderAbortError(signal);
    }
    throw error;
  }
  return { count, bytes };
}

function normalizePacketTiming(
  packet: EncodedPacket,
  sourceStartTimestamp: number,
  outputDurationSeconds: number
): EncodedPacket | null {
  const rawTimestamp = packet.timestamp - sourceStartTimestamp;
  const rawEnd = rawTimestamp + packet.duration;
  if (rawEnd <= 0 || rawTimestamp >= outputDurationSeconds) {
    return null;
  }

  const timestamp = Math.max(0, rawTimestamp);
  const duration = Math.min(rawEnd, outputDurationSeconds) - timestamp;
  if (!Number.isFinite(duration) || duration <= 0) {
    return null;
  }
  return packet.clone({ timestamp, duration });
}
